import { useState, useEffect } from 'react';
import Navbar from '../services/components/Navbar';
import Footer from '../services/components/Footer';
import SEOHead from '../../components/SEOHead';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    propertyAddress: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Aire University",
    "image": `${siteUrl}/vite.svg`,
    "description": "Expert Airbnb property setup and management services in Newark, Delaware. Transform your property into a profitable short-term rental.",
    "url": siteUrl,
    "telephone": "+1-302-500-3373",
    "email": "Aireuniversity@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2043 Pulaski Hwy",
      "addressLocality": "Newark",
      "addressRegion": "DE",
      "postalCode": "19702",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "39.6789012",
      "longitude": "-75.7234567"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "priceRange": "$299-$2999",
    "areaServed": {
      "@type": "State",
      "name": "Delaware"
    }
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Aire University",
    "description": "Get in touch with Aire University for Airbnb property setup services. Located at 2043 Pulaski Hwy, Newark, DE 19702. Call 302-500-3373 or email Aireuniversity@gmail.com",
    "url": `${siteUrl}/contact`
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [localBusinessSchema, contactPageSchema]
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5i7fg1ia7n1eqcquti0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          propertyAddress: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Aire University Newark DE | 302-500-3373 | Airbnb Setup Consultation"
        description="Contact Aire University for expert Airbnb property setup services in Newark, Delaware. Call 302-500-3373, email Aireuniversity@gmail.com, or visit us at 2043 Pulaski Hwy. Free consultation available Monday-Saturday."
        keywords="contact Aire University, Airbnb consultation Newark, property setup contact, Newark DE Airbnb services, free consultation"
        schema={combinedSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to transform your property into a profitable Airbnb? We're here to help you every step of the way.
            </p>
          </div>
        </section>

        {/* Schedule Consultation Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Schedule Your Free Consultation</h2>
              <p className="text-xl text-gray-600">
                Book a time that works best for you - we're excited to discuss your property's potential!
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style={{ minHeight: '700px' }}>
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/zhd8714/new-meeting" 
                style={{ minWidth: '320px', height: '700px' }}
              ></div>
            </div>
          </div>
        </section>

        {/* Contact Info & Form Section */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Other Ways to Reach Us</h2>
              <p className="text-xl text-gray-600">
                Prefer a different method? We're available through multiple channels.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              
              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h3>
                  
                  <div className="space-y-6">
                    {/* Address */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-coral-50 rounded-lg flex-shrink-0">
                        <i className="ri-map-pin-line text-2xl text-coral-500"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">Address</h4>
                        <p className="text-gray-600">2043 Pulaski Hwy<br />Newark, DE 19702</p>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-coral-50 rounded-lg flex-shrink-0">
                        <i className="ri-phone-line text-2xl text-coral-500"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">Phone</h4>
                        <a href="tel:302-500-3373" className="text-gray-600 hover:text-coral-500 transition-colors cursor-pointer">
                          302-500-3373
                        </a>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-coral-50 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-2xl text-coral-500"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">Email</h4>
                        <a href="mailto:Aireuniversity@gmail.com" className="text-gray-600 hover:text-coral-500 transition-colors cursor-pointer">
                          Aireuniversity@gmail.com
                        </a>
                      </div>
                    </div>

                    {/* Business Hours */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-coral-50 rounded-lg flex-shrink-0">
                        <i className="ri-time-line text-2xl text-coral-500"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h4>
                        <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM<br />Saturday: 10:00 AM - 4:00 PM<br />Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3067.8947234567!2d-75.7234567!3d39.6789012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c7043f1234abcd%3A0x1234567890abcdef!2s2043%20Pulaski%20Hwy%2C%20Newark%2C%20DE%2019702!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Aire University Location"
                  ></iframe>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gray-50 rounded-2xl p-8 lg:p-10">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h3>
                <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all"
                      placeholder="John Smith"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all"
                      placeholder="(302) 555-0123"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Property Address */}
                  <div>
                    <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-900 mb-2">
                      Property Address *
                    </label>
                    <input
                      type="text"
                      id="propertyAddress"
                      name="propertyAddress"
                      value={formData.propertyAddress}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all"
                      placeholder="123 Main Street, City, State ZIP"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your property and what services you're interested in..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-coral-500 text-white font-semibold py-4 px-8 rounded-lg hover:bg-coral-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <p className="font-semibold">Message sent successfully!</p>
                      <p className="text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <p className="font-semibold">Something went wrong.</p>
                      <p className="text-sm">Please try again or call us directly.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-coral-500 to-coral-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Schedule a free consultation today and discover how we can help you maximize your property's potential.
            </p>
            <a
              href="tel:302-500-3373"
              className="inline-block bg-white text-coral-500 font-semibold py-4 px-10 rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Call Us Now: 302-500-3373
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
